import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import {
  FiSearch, FiShoppingBag, FiUser, FiMenu, FiX, FiChevronDown, FiLogOut,
  FiGrid, FiChevronRight, FiHeart
} from 'react-icons/fi';
import { useSiteSettings } from '../context/SiteSettingsContext';
import SearchBar from './SearchBar';

// Category data with emoji icons
const CATEGORIES = [
  { name: 'Handbags', icon: '👜', desc: 'Shoulder bags, sling bags and more' },
  { name: 'Tote Bags', icon: '🛍️', desc: 'Canvas, leather and office totes' },
  { name: 'Clutches', icon: '✨', desc: 'Party clutches and wristlets' },
  { name: 'Backpacks', icon: '🎒', desc: 'Mini, college and travel backpacks' },
  { name: 'Wallets', icon: '💰', desc: 'Bifold, card holders and zip-arounds' },
  { name: 'Sling Bags', icon: '👝', desc: 'Crossbody, mini and chain slings' },
  { name: 'Travel Bags', icon: '🧳', desc: 'Duffles, weekenders and passport holders' },
  { name: 'Potli Bags', icon: '🎀', desc: 'Ethnic, bridal and festive potlis' },
];

const Navbar = () => {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { count: wishlistCount } = useWishlist();
  const { appearance } = useSiteSettings();
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [mobileShopExpanded, setMobileShopExpanded] = useState(false);

  const userMenuRef = useRef(null);
  const megaMenuRef = useRef(null);
  const megaMenuTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setMegaMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/products', hasMegaMenu: true },
    { name: 'About', path: '/#about' },
    { name: 'Contact', path: '/#contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/' && location.hash === '';
    if (path.startsWith('/#')) return location.pathname === '/' && location.hash === path.substring(1);
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (e, path, isMobile = false) => {
    if (isMobile) setMobileOpen(false);
    
    if (path === '/') {
      if (location.pathname === '/') {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate('/');
      }
    } else if (path.startsWith('/#') && location.pathname === '/') {
      e.preventDefault();
      const targetId = path.substring(2);
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        window.history.pushState(null, '', path);
      }
    }
  };

  const handleMegaMenuEnter = () => {
    if (megaMenuTimeoutRef.current) clearTimeout(megaMenuTimeoutRef.current);
    setMegaMenuOpen(true);
  };

  const handleMegaMenuLeave = () => {
    megaMenuTimeoutRef.current = setTimeout(() => {
      setMegaMenuOpen(false);
    }, 200);
  };

  const logoSettings = appearance?.logo || {};
  const announcement = appearance?.announcement || {};

  const getValidLogoUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    let cleanUrl = url.replace(/^["']|["']$/g, '').trim();
    if (/^[A-Za-z]:[/\\]/.test(cleanUrl) || cleanUrl.startsWith('file://')) return null;
    if (cleanUrl.startsWith('http') || cleanUrl.startsWith('data:')) return cleanUrl;
    return `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${cleanUrl.startsWith('/') ? '' : '/'}${cleanUrl}`;
  };

  const logoUrl = getValidLogoUrl(logoSettings.image);

  return (
    <>
    {/* Announcement Bar */}
    {announcement.isActive && announcement.text && (
      <div 
        className="fixed top-0 left-0 right-0 z-[60] text-center py-2 px-4 text-xs font-medium tracking-wide"
        style={{ backgroundColor: announcement.bgColor || '#1A1A2E', color: announcement.textColor || '#ffffff' }}
      >
        {announcement.text}
      </div>
    )}

    <nav className={`fixed ${announcement.isActive && announcement.text ? 'top-8' : 'top-0'} left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-charcoal hover:text-gold transition-colors"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>

          {/* Logo */}
          <Link to="/" onClick={(e) => handleNavClick(e, '/')} className="flex flex-col items-center justify-center pt-1 hover:opacity-90 transition-opacity">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt={logoSettings.storeName || "Poshe Styles"} 
                className="max-h-10 md:max-h-12" 
              />
            ) : (
              <>
                <h1 className="text-3xl md:text-4xl font-script text-gold" style={{ lineHeight: 0.8, textShadow: '0 1px 2px rgba(0,0,0,0.1)', color: 'var(--primary)' }}>
                  {logoSettings.storeName || "Poshe Styles"}
                </h1>
                <span className="text-[9px] md:text-[10px] font-cinzel text-gold tracking-[0.2em] uppercase mt-1" style={{ color: 'var(--primary)' }}>
                  Design Studio
                </span>
              </>
            )}
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <div
                key={link.name}
                className="relative"
                onMouseEnter={link.hasMegaMenu ? handleMegaMenuEnter : undefined}
                onMouseLeave={link.hasMegaMenu ? handleMegaMenuLeave : undefined}
                ref={link.hasMegaMenu ? megaMenuRef : undefined}
              >
                <Link
                  to={link.path}
                  onClick={(e) => handleNavClick(e, link.path, false)}
                  className={`font-medium text-sm tracking-wide transition-colors duration-200 relative group
                    flex items-center gap-1
                    ${isActive(link.path) ? 'text-gold' : 'text-charcoal hover:text-gold'}`}
                >
                  {link.name}
                  {link.hasMegaMenu && (
                    <FiChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${megaMenuOpen ? 'rotate-180' : ''}`}
                    />
                  )}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-gold transition-all duration-300
                                   ${isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'}`} />
                </Link>
              </div>
            ))}
          </div>

          {/* Right icons */}
          <div className="flex items-center gap-3 md:gap-5">
            {/* Search */}
            <div className="relative">
              {searchOpen ? (
                <SearchBar onClose={() => setSearchOpen(false)} />
              ) : (
                <button
                  onClick={() => setSearchOpen(true)}
                  className="text-charcoal hover:text-gold transition-colors"
                  aria-label="Search"
                  id="nav-search-btn"
                >
                  <FiSearch size={20} />
                </button>
              )}
            </div>

            {/* Cart */}
            <Link to="/cart" aria-label={`Cart with ${count} items`} className="relative text-charcoal hover:text-gold transition-colors">
              <FiShoppingBag size={20} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold text-white text-xs
                                 w-5 h-5 rounded-full flex items-center justify-center
                                 font-semibold animate-pulse">
                  {count}
                </span>
              )}
            </Link>

            {/* Wishlist */}
            <Link to="/wishlist" aria-label={`Wishlist with ${wishlistCount} items`} className="relative text-charcoal hover:text-red-500 transition-colors">
              <FiHeart size={20} fill={wishlistCount > 0 ? '#ef4444' : 'none'}
                       className={wishlistCount > 0 ? 'text-red-500' : ''} />
              {wishlistCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs
                                 w-5 h-5 rounded-full flex items-center justify-center
                                 font-semibold">
                  {wishlistCount}
                </span>
              )}
            </Link>

            {/* User menu */}
            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-1 text-charcoal hover:text-gold transition-colors"
                >
                  <div className="w-8 h-8 bg-gold text-white rounded-full flex items-center
                                  justify-center text-sm font-semibold">
                    {user.name?.charAt(0).toUpperCase()}
                  </div>
                  <FiChevronDown size={16} className={`hidden md:block transition-transform ${
                    userMenuOpen ? 'rotate-180' : ''
                  }`} />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-3 w-56 bg-white rounded-xl shadow-xl
                                  border border-gray-100 py-2 animate-fadeInUp z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="font-semibold text-charcoal">{user.name}</p>
                      <p className="text-xs text-gray-400">{user.email}</p>
                    </div>
                    <Link
                      to="/account"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-charcoal
                                 hover:bg-offwhite transition-colors"
                    >
                      <FiUser size={16} /> My Account
                    </Link>
                    <Link
                      to="/wishlist"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-charcoal
                                 hover:bg-offwhite transition-colors"
                    >
                      <FiHeart size={16} /> My Wishlist
                      {wishlistCount > 0 && (
                        <span className="ml-auto bg-red-100 text-red-500 text-[10px] px-1.5 py-0.5
                                       rounded-full font-semibold">
                          {wishlistCount}
                        </span>
                      )}
                    </Link>
                    {user.role === 'admin' && (
                      <Link
                        to="/poshe-admin/dashboard"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-charcoal
                                   hover:bg-offwhite transition-colors"
                      >
                        <FiGrid size={16} /> Admin Panel
                      </Link>
                    )}
                    <button
                      onClick={() => { logout(); setUserMenuOpen(false); }}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-500
                                 hover:bg-red-50 w-full transition-colors"
                    >
                      <FiLogOut size={16} /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-1 text-charcoal hover:text-gold transition-colors"
              >
                <FiUser size={20} />
                <span className="hidden md:inline text-sm font-medium">Login</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* ─── MEGA MENU DROPDOWN (Desktop) ─── */}
      {megaMenuOpen && (
        <div
          className="hidden md:block absolute left-0 right-0 bg-white shadow-2xl border-t border-gray-100
                     animate-fadeInUp z-40"
          style={{ animationDuration: '0.15s' }}
          onMouseEnter={handleMegaMenuEnter}
          onMouseLeave={handleMegaMenuLeave}
          id="mega-menu-panel"
        >
          <div className="max-w-7xl mx-auto px-8 py-8">
            <div className="grid grid-cols-4 gap-4">
              {CATEGORIES.map((cat) => (
                <Link
                  key={cat.name}
                  to={`/products?category=${encodeURIComponent(cat.name)}`}
                  className="group flex items-center gap-4 p-4 rounded-xl hover:bg-offwhite
                           transition-all duration-200 hover:shadow-sm"
                  onClick={() => setMegaMenuOpen(false)}
                >
                  <span className="text-3xl group-hover:scale-110 transition-transform duration-200">
                    {cat.icon}
                  </span>
                  <div>
                    <h4 className="font-semibold text-sm text-charcoal group-hover:text-gold
                                 transition-colors">
                      {cat.name}
                    </h4>
                    <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-1">{cat.desc}</p>
                  </div>
                </Link>
              ))}
            </div>

            {/* Bottom strip */}
            <div className="mt-6 pt-5 border-t border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-6">
                <Link
                  to="/products"
                  onClick={() => setMegaMenuOpen(false)}
                  className="text-sm font-semibold text-gold hover:text-gold-dark transition-colors
                           flex items-center gap-1"
                >
                  View All Products <FiChevronRight size={14} />
                </Link>
                <Link
                  to="/products?sort=newest"
                  onClick={() => setMegaMenuOpen(false)}
                  className="text-sm text-gray-500 hover:text-charcoal transition-colors"
                >
                  New Arrivals
                </Link>
                <Link
                  to="/products?sort=popular"
                  onClick={() => setMegaMenuOpen(false)}
                  className="text-sm text-gray-500 hover:text-charcoal transition-colors"
                >
                  Best Sellers
                </Link>
              </div>
              <p className="text-xs text-gray-400">
                🚚 Free shipping on orders above ₹999
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ─── MOBILE MENU ─── */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg animate-fadeInUp
                       max-h-[85vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {/* Mobile search */}
            <div className="mb-4">
              <SearchBar
                isMobile={true}
                onClose={() => setMobileOpen(false)}
              />
            </div>

            {navLinks.map((link) => (
              <div key={link.name}>
                {link.hasMegaMenu ? (
                  <>
                    <button
                      onClick={() => setMobileShopExpanded(!mobileShopExpanded)}
                      className={`flex items-center justify-between w-full px-3 py-3 rounded-lg
                                font-medium transition-colors
                                ${isActive(link.path) ? 'text-gold bg-offwhite' : 'text-charcoal hover:text-gold hover:bg-offwhite'}`}
                    >
                      {link.name}
                      <FiChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${mobileShopExpanded ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {/* Mobile category accordion */}
                    {mobileShopExpanded && (
                      <div className="pl-4 pb-2 space-y-0.5 animate-fadeInUp"
                           style={{ animationDuration: '0.2s' }}>
                        {CATEGORIES.map((cat) => (
                          <Link
                            key={cat.name}
                            to={`/products?category=${encodeURIComponent(cat.name)}`}
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
                                     text-gray-600 hover:text-gold hover:bg-offwhite transition-colors"
                          >
                            <span className="text-lg">{cat.icon}</span>
                            <span>{cat.name}</span>
                          </Link>
                        ))}
                        <Link
                          to="/products"
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold
                                   text-gold hover:bg-offwhite rounded-lg transition-colors"
                        >
                          View All <FiChevronRight size={14} />
                        </Link>
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.path}
                    onClick={(e) => handleNavClick(e, link.path, true)}
                    className={`block px-3 py-3 rounded-lg font-medium transition-colors
                      ${isActive(link.path) ? 'text-gold bg-offwhite' : 'text-charcoal hover:text-gold hover:bg-offwhite'}`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            {!user && (
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-3 text-gold font-semibold hover:bg-offwhite
                           rounded-lg transition-colors"
              >
                Login / Register
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

export default Navbar;
